import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

// Public page opened from the WhatsApp "Register" URL button. Route: /register/:token

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C5A059';
const fontHead = { fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontWeight: 300, letterSpacing: '-0.01em' };
const fontBody = { fontFamily: "'Manrope', 'Inter', system-ui, sans-serif" };

const inputCls = 'w-full h-12 px-4 rounded-xl border border-stone-300 bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#C5A059]/60 focus:border-[#C5A059] transition';
const labelCls = 'block text-xs font-bold uppercase tracking-[0.18em] text-stone-500 mb-2';

const RegisterPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState({ loading: true, err: null, data: null });
  const [form, setForm] = useState({ name: '', gender: '', dobMode: 'dob', dob: '', age: '', email: '', address: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&family=Manrope:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    document.body.style.backgroundColor = '#F5F2EA';
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/register/${token}`);
        setInfo({ loading: false, err: null, data: res.data });
      } catch (e) {
        setInfo({ loading: false, err: e.response?.data?.detail || 'This link is not valid', data: null });
      }
    })();
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (form.name.trim().length < 2) return setErr('Please enter your full name');
    if (!form.gender) return setErr('Please select your gender');
    if (form.dobMode === 'dob' && !form.dob) return setErr('Please enter your date of birth');
    if (form.dobMode === 'age' && !(parseInt(form.age, 10) > 0)) return setErr('Please enter your age');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/public/register/${token}`, {
        name: form.name.trim(),
        gender: form.gender,
        dob: form.dobMode === 'dob' ? form.dob : null,
        age: form.dobMode === 'age' ? parseInt(form.age, 10) : null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
      });
      setDone(res.data.name);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Could not submit. Please try again.');
    } finally { setSubmitting(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen px-5 py-12 sm:py-20" style={{ background: '#F5F2EA', ...fontBody }}>
      <div className="max-w-xl mx-auto" data-testid="register-page">
        <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          {info.data?.clinic_name || 'Eden Skin Clinic'} · Registration
        </div>
        {children}
        <div className="mt-12 pt-6 border-t text-sm text-stone-500" style={{ borderColor: 'rgba(197,160,89,0.25)' }}>
          Need help? Call us at <a href="tel:+917710117777" className="font-semibold text-emerald-700">+91 77101 17777</a>
        </div>
      </div>
    </div>
  );

  if (info.loading) {
    return shell(<div className="mt-16 flex items-center gap-3 text-stone-500"><Loader2 className="w-5 h-5 animate-spin" /> Opening your form…</div>);
  }
  if (info.err || (info.data?.status !== 'sent' && info.data?.status !== 'registered')) {
    return shell(
      <>
        <h1 className="mt-6 text-4xl sm:text-5xl text-stone-900" style={fontHead}>This link isn't active</h1>
        <p className="mt-4 text-stone-600 flex items-start gap-2" data-testid="register-error"><AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />{info.err || 'This registration link is no longer active.'}</p>
      </>
    );
  }
  if (done || info.data?.status === 'registered') {
    return shell(
      <>
        <div className="mt-10 w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-8 h-8 text-emerald-600" /></div>
        <h1 className="mt-6 text-4xl sm:text-5xl text-stone-900" style={fontHead}>Welcome, {done || info.data.registered_name}</h1>
        <p className="mt-4 text-stone-600" data-testid="register-success">You're registered. Our front desk will book your appointment — see you at the clinic soon.</p>
      </>
    );
  }

  return shell(
    <>
      <h1 className="mt-6 text-4xl sm:text-5xl text-stone-900" style={fontHead}>Welcome. Let's get you registered.</h1>
      <p className="mt-3 text-stone-600">A minute of your time now makes every visit smoother. Registering for <span className="font-semibold text-stone-800">{info.data.phone_masked}</span>.</p>

      <form onSubmit={submit} className="mt-10 space-y-7" noValidate>
        <div>
          <label className={labelCls} htmlFor="reg-name">Full name *</label>
          <input id="reg-name" data-testid="register-name-input" className={inputCls} value={form.name} onChange={set('name')} placeholder="e.g. Priya Sharma" autoComplete="name" />
        </div>

        <div>
          <span className={labelCls}>Gender *</span>
          <div className="grid grid-cols-3 gap-2" data-testid="register-gender-group">
            {['Female', 'Male', 'Other'].map((g) => (
              <button type="button" key={g} data-testid={`register-gender-${g.toLowerCase()}`}
                onClick={() => setForm((f) => ({ ...f, gender: g }))}
                className={`h-12 rounded-xl border text-sm font-semibold transition-colors ${form.gender === g ? 'bg-stone-900 text-white border-stone-900' : 'bg-white border-stone-300 text-stone-700 hover:border-stone-500'}`}>
                {g}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className={`${labelCls} mb-0`}>{form.dobMode === 'dob' ? 'Date of birth *' : 'Age *'}</span>
            <button type="button" data-testid="register-dob-toggle" onClick={() => setForm((f) => ({ ...f, dobMode: f.dobMode === 'dob' ? 'age' : 'dob' }))} className="text-xs font-semibold underline underline-offset-4" style={{ color: GOLD }}>
              {form.dobMode === 'dob' ? "Don't know DOB? Enter age" : 'Enter date of birth instead'}
            </button>
          </div>
          {form.dobMode === 'dob'
            ? <input type="date" data-testid="register-dob-input" className={inputCls} value={form.dob} onChange={set('dob')} max={new Date().toISOString().slice(0, 10)} />
            : <input type="number" data-testid="register-age-input" className={inputCls} value={form.age} onChange={set('age')} placeholder="Years" min="1" max="119" />}
        </div>

        <div>
          <label className={labelCls} htmlFor="reg-email">Email <span className="normal-case tracking-normal font-medium text-stone-400">(optional)</span></label>
          <input id="reg-email" type="email" data-testid="register-email-input" className={inputCls} value={form.email} onChange={set('email')} placeholder="you@example.com" autoComplete="email" />
        </div>

        <div>
          <label className={labelCls} htmlFor="reg-address">Address <span className="normal-case tracking-normal font-medium text-stone-400">(optional)</span></label>
          <textarea id="reg-address" data-testid="register-address-input" className={`${inputCls} h-24 py-3 resize-none`} value={form.address} onChange={set('address')} placeholder="Area, City" />
        </div>

        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="register-form-error">{err}</p>}

        <button type="submit" disabled={submitting} data-testid="register-submit-button"
          className="w-full h-14 rounded-full bg-stone-900 text-white font-semibold tracking-wide hover:bg-stone-800 active:scale-[0.99] transition disabled:opacity-60 flex items-center justify-center gap-2">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitting ? 'Submitting…' : 'Complete registration'}
        </button>
        <p className="text-xs text-stone-500 text-center">Your details stay private and are used only for your care at the clinic.</p>
      </form>
    </>
  );
};

export default RegisterPage;
